import PocketBase from 'pocketbase';

// Instance PocketBase configurée avec votre URL backend
const pb = new PocketBase(process.env.REACT_APP_POCKETBASE_URL || 'https://studioambre.infocolab.online');

// Configuration optionnelle
pb.autoCancellation(false); // Désactive l'annulation automatique des requêtes

/**
 * Service PocketBase pour Studio Ambre
 * 
 * Ce service centralise toutes les interactions avec la base de données PocketBase.
 * Il expose des méthodes pour gérer :
 * - L'authentification
 * - Les rendez-vous/réservations
 * - Les services
 * - La galerie
 * - Les produits
 */

// ===== AUTHENTIFICATION =====

/**
 * Connecte un utilisateur (Admin ou Client) avec email/pass
 * @param {string} email
 * @param {string} password
 * @returns {Promise}
 */
export const login = async (email, password) => {
    try {
        const authData = await pb.collection('users').authWithPassword(email, password);
        return authData;
    } catch (error) {
        console.error('Erreur de connexion:', error);
        throw error;
    }
};

/**
 * Alias pour login (rétrocompatibilité)
 */
export const loginAdmin = login;

/**
 * Inscrit un nouvel utilisateur
 * @param {Object} userData - { email, password, passwordConfirm, name, ... }
 * @returns {Promise}
 */
export const register = async (userData) => {
    try {
        const record = await pb.collection('users').create(userData);
        return record;
    } catch (error) {
        console.error('Erreur d\'inscription:', error);
        throw error;
    }
};

/**
 * Déconnecte l'utilisateur actuel
 */
export const logout = () => {
    pb.authStore.clear();
};

/**
 * Vérifie si un utilisateur est connecté
 * @returns {boolean}
 */
export const isAuthenticated = () => {
    return pb.authStore.isValid;
};

/**
 * Récupère l'utilisateur actuellement connecté
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
    return pb.authStore.model;
};

/**
 * Récupère tous les utilisateurs
 * @returns {Promise}
 */
export const getUsers = async () => {
    try {
        const records = await pb.collection('users').getFullList({
            sort: '-created'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        throw error;
    }
};

// ===== RÉSERVATIONS / RENDEZ-VOUS =====

/**
 * Crée une nouvelle réservation
 * @param {Object} bookingData - Données de la réservation
 * @returns {Promise}
 */
export const createBooking = async (bookingData) => {
    try {
        const record = await pb.collection('active').create(bookingData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création de la réservation:', error);
        throw error;
    }
};

/**
 * Récupère toutes les réservations
 * @param {Object} options - Options de filtrage et pagination
 * @returns {Promise}
 */
export const getBookings = async (options = {}) => {
    try {
        const records = await pb.collection('active').getList(1, 50, {
            sort: '-created',
            ...options
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des réservations:', error);
        throw error;
    }
};

// ... (getBookings est avant)

/**
 * Récupère les réservations pour une date spécifique (toute la journée)
 * @param {Date} date - La date à vérifier
 * @returns {Promise}
 */
export const getBookingsForDate = async (date) => {
    try {
        // Début de la journée (00:00:00)
        const start = new Date(date);
        start.setHours(0, 0, 0, 0);

        // Fin de la journée (23:59:59)
        const end = new Date(date);
        end.setHours(23, 59, 59, 999);

        // Format PocketBase UTC requis pour le filtrage
        // Astuce : PB utilise des dates au format string "2024-01-01 10:00:00"
        // Le filter compare en UTC.

        // La méthode la plus simple est de récupérer les bookings du mois ou large et filtrer en JS,
        // mais c'est moins performant si beaucoup de bookings.

        // Filtre PB direct :
        const records = await pb.collection('active').getFullList({
            filter: `date >= "${start.toISOString()}" && date <= "${end.toISOString()}"`,
            expand: 'service', // Important pour avoir la durée du service
        });

        return records;
    } catch (error) {
        console.error('Erreur getBookingsForDate:', error);
        return [];
    }
};

/**
 * Met à jour une réservation
 // ... * @param {string} id - ID de la réservation
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise}
 */
export const updateBooking = async (id, data) => {
    try {
        const record = await pb.collection('active').update(id, data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour de la réservation:', error);
        throw error;
    }
};

/**
 * Supprime une réservation
 * @param {string} id - ID de la réservation
 * @returns {Promise}
 */
export const deleteBooking = async (id) => {
    try {
        await pb.collection('active').delete(id);
    } catch (error) {
        console.error('Erreur lors de la suppression de la réservation:', error);
        throw error;
    }
};

// ===== SERVICES =====

/**
 * Récupère tous les services
 * @returns {Promise}
 */
/**
 * Récupère tous les services (triés par ordre)
 * @returns {Promise}
 */
export const getServices = async () => {
    try {
        const records = await pb.collection('services').getFullList({
            sort: 'order',
            expand: 'category'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des services:', error);
        throw error;
    }
};

/**
 * Crée un nouveau service
 * @param {Object} serviceData - Données du service
 * @returns {Promise}
 */
export const createService = async (serviceData) => {
    try {
        const record = await pb.collection('services').create(serviceData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création du service:', error);
        throw error;
    }
};

/**
 * Met à jour un service
 * @param {string} id - ID du service
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise}
 */
export const updateService = async (id, data) => {
    try {
        const record = await pb.collection('services').update(id, data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du service:', error);
        throw error;
    }
};

/**
 * Supprime un service
 * @param {string} id - ID du service
 * @returns {Promise}
 */
export const deleteService = async (id) => {
    try {
        await pb.collection('services').delete(id);
    } catch (error) {
        console.error('Erreur lors de la suppression du service:', error);
        throw error;
    }
};

// ===== CATÉGORIES DE SERVICES =====

/**
 * Récupère toutes les catégories de services
 * @returns {Promise}
 */
export const getCategories = async () => {
    try {
        const records = await pb.collection('categories').getFullList({
            sort: 'order'
        });
        return records;
    } catch (error) {
        // Si la collection n'existe pas, on ne plante pas tout, on renvoie vide
        console.warn('Collection categories non trouvée ou vide');
        return [];
    }
};

/**
 * Crée une nouvelle catégorie
 * @param {Object} data
 * @returns {Promise}
 */
export const createCategory = async (data) => {
    return await pb.collection('categories').create(data);
};

/**
 * Met à jour une catégorie
 * @param {string} id
 * @param {Object} data
 * @returns {Promise}
 */
export const updateCategory = async (id, data) => {
    return await pb.collection('categories').update(id, data);
};

/**
 * Supprime une catégorie
 * @param {string} id
 * @returns {Promise}
 */
export const deleteCategory = async (id) => {
    return await pb.collection('categories').delete(id);
};

// ===== GALERIE =====

/**
 * Récupère toutes les images de la galerie
 * @returns {Promise}
 */
export const getGalleryImages = async () => {
    try {
        const records = await pb.collection('gallery').getFullList({
            sort: 'order'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération de la galerie:', error);
        throw error;
    }
};

/**
 * Upload une nouvelle image dans la galerie
 * @param {Object} imageData - Données de l'image (incluant le fichier)
 * @returns {Promise}
 */
export const uploadGalleryImage = async (imageData) => {
    try {
        const formData = new FormData();
        Object.keys(imageData).forEach(key => {
            formData.append(key, imageData[key]);
        });
        const record = await pb.collection('gallery').create(formData);
        return record;
    } catch (error) {
        console.error('Erreur lors de l\'upload de l\'image:', error);
        throw error;
    }
};

/**
 * Met à jour une image de la galerie (ex: pour l'ordre)
 * @param {string} id
 * @param {Object} data
 * @returns {Promise}
 */
export const updateGalleryImage = async (id, data) => {
    try {
        const record = await pb.collection('gallery').update(id, data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour de l\'image:', error);
        throw error;
    }
};

/**
 * Supprime une image de la galerie
 * @param {string} id - ID de l'image
 * @returns {Promise}
 */
export const deleteGalleryImage = async (id) => {
    try {
        await pb.collection('gallery').delete(id);
    } catch (error) {
        console.error('Erreur lors de la suppression de l\'image:', error);
        throw error;
    }
};

/**
 * Récupère l'URL d'une image
 * @param {Object} record - Enregistrement de l'image
 * @param {string} filename - Nom du fichier
 * @returns {string} URL de l'image
 */
export const getImageUrl = (record, filename) => {
    return pb.files.getUrl(record, filename);
};

// ===== PRODUITS =====

/**
 * Récupère tous les produits
 * @param {string} category - Catégorie de produits (optionnel: 'umai', 'gaia')
 * @returns {Promise}
 */
export const getProducts = async (category = null) => {
    try {
        const filter = category ? `category = "${category}"` : '';
        const records = await pb.collection('products').getFullList({
            filter,
            sort: 'order'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des produits:', error);
        throw error;
    }
};

/**
 * Crée un nouveau produit
 * @param {Object} productData - Données du produit
 * @returns {Promise}
 */
export const createProduct = async (productData) => {
    try {
        const record = await pb.collection('products').create(productData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création du produit:', error);
        throw error;
    }
};

/**
 * Met à jour un produit
 * @param {string} id - ID du produit
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise}
 */
export const updateProduct = async (id, data) => {
    try {
        const record = await pb.collection('products').update(id, data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour du produit:', error);
        throw error;
    }
};

/**
 * Supprime un produit
 * @param {string} id - ID du produit
 * @returns {Promise}
 */
export const deleteProduct = async (id) => {
    try {
        await pb.collection('products').delete(id);
    } catch (error) {
        console.error('Erreur lors de la suppression du produit:', error);
        throw error;
    }
};

// ===== HORAIRES D'OUVERTURE =====

/**
 * Récupère les horaires d'ouverture
 * @returns {Promise}
 */
export const getOpeningHours = async () => {
    try {
        const records = await pb.collection('opening_hours').getFullList({
            sort: 'day'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des horaires:', error);
        throw error;
    }
};

/**
 * Crée un nouvel horaire d'ouverture
 * @param {Object} data - Données de l'horaire
 * @returns {Promise}
 */
export const createOpeningHour = async (data) => {
    try {
        const record = await pb.collection('opening_hours').create(data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création de l\'horaire:', error);
        throw error;
    }
};

/**
 * Met à jour les horaires d'ouverture
 * @param {string} id - ID de l'horaire
 * @param {Object} data - Données à mettre à jour
 * @returns {Promise}
 */
export const updateOpeningHours = async (id, data) => {
    try {
        const record = await pb.collection('opening_hours').update(id, data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour des horaires:', error);
        throw error;
    }
};

// ===== TÉMOIGNAGES =====

/**
 * Récupère tous les témoignages
 * @returns {Promise}
 */
export const getTestimonials = async () => {
    try {
        const records = await pb.collection('testimonials').getFullList({
            sort: '-created',
            filter: 'approved = true'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des témoignages:', error);
        throw error;
    }
};

/**
 * Crée un nouveau témoignage
 * @param {Object} testimonialData - Données du témoignage
 * @returns {Promise}
 */
export const createTestimonial = async (testimonialData) => {
    try {
        const record = await pb.collection('testimonials').create(testimonialData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création du témoignage:', error);
        throw error;
    }
};

// ===== SALON INFOS =====

/**
 * Récupère les informations du salon (singleton)
 * @returns {Promise<Object>}
 */
export const getSalonInfos = async () => {
    try {
        const records = await pb.collection('salon_infos').getList(1, 1);
        return records.items.length > 0 ? records.items[0] : null;
    } catch (error) {
        console.warn('Erreur récupération infos salon (peut-être vide):', error);
        return null;
    }
};

/**
 * Met à jour ou crée les informations du salon
 * @param {string} id - ID existant (optionnel si c'est une création)
 * @param {Object} data - Données
 * @returns {Promise}
 */
export const updateSalonInfos = async (id, data) => {
    try {
        if (id) {
            return await pb.collection('salon_infos').update(id, data);
        } else {
            // Check if one exists to be safe
            const existing = await getSalonInfos();
            if (existing) {
                return await pb.collection('salon_infos').update(existing.id, data);
            }
            return await pb.collection('salon_infos').create(data);
        }
    } catch (error) {
        console.error('Erreur mise à jour infos salon:', error);
        throw error;
    }
};

// ===== DATES BLOQUÉES (CONGÉS) =====

/**
 * Récupère les dates bloquées
 * @returns {Promise}
 */
export const getBlockedDates = async () => {
    try {
        const records = await pb.collection('blocked_dates').getFullList({
            sort: '-date'
        });
        return records;
    } catch (error) {
        console.error('Erreur lors de la récupération des dates bloquées:', error);
        throw error;
    }
};

/**
 * Crée une date bloquée
 * @param {Object} data - { date, reason }
 * @returns {Promise}
 */
export const createBlockedDate = async (data) => {
    try {
        const record = await pb.collection('blocked_dates').create(data);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création de la date bloquée:', error);
        throw error;
    }
};

/**
 * Supprime une date bloquée
 * @param {string} id
 * @returns {Promise}
 */
export const deleteBlockedDate = async (id) => {
    try {
        await pb.collection('blocked_dates').delete(id);
    } catch (error) {
        console.error('Erreur lors de la suppression de la date bloquée:', error);
        throw error;
    }
};

// Export de l'instance PocketBase pour un usage avancé
export default pb;
